import type { KnowledgeEntry } from '../types/foreman';
import type { UserRole } from '../types/gateway';
import type { JobThreadWithAcl } from '../hooks/useThreads';
import { useThreadSearch } from '../hooks/useThreadSearch';
import { useState } from 'react';
import { JobThreadItem } from './JobThread';
import { KnowledgePanel } from './KnowledgePanel';
import { ThreadSearchResults } from './ThreadSearchResults';
import styles from './Sidebar.module.css';

interface SidebarProps {
  myThreads: JobThreadWithAcl[];
  sharedWithMe: JobThreadWithAcl[];
  activeThreadId: string | null;
  onSelectThread: (id: string) => void;
  onDeleteThread?: (sessionKey: string) => void;
  onShareThread?: (sessionKey: string) => void;
  onRenameThread?: (sessionKey: string) => void;
  currentRole: UserRole;
  isAdmin: boolean;
  sidebarTab: 'threads' | 'knowledge';
  onTabChange: (tab: 'threads' | 'knowledge') => void;
  knowledgeEntries: KnowledgeEntry[];
  onSearchKnowledge: (query: string) => void;
  sidebarOpen?: boolean;
}

export function Sidebar({
  myThreads,
  sharedWithMe,
  activeThreadId,
  onSelectThread,
  onDeleteThread,
  onShareThread,
  onRenameThread,
  currentRole: _currentRole,
  isAdmin,
  sidebarTab,
  onTabChange,
  knowledgeEntries,
  onSearchKnowledge,
  sidebarOpen = true,
}: SidebarProps) {
  const search = useThreadSearch();
  const [knowledgeSearch, setKnowledgeSearch] = useState('');

  // Client-side filtering only kicks in for sub-min-len queries (1 char) so
  // typing feels responsive before the server search activates.
  const trimmed = search.query.trim();
  const matches = (t: JobThreadWithAcl) =>
    trimmed.length === 0 ||
    t.name.toLowerCase().includes(trimmed.toLowerCase()) ||
    t.code.toLowerCase().includes(trimmed.toLowerCase());

  const filteredMine = myThreads.filter(matches);
  const filteredShared = sharedWithMe.filter(matches);
  const totalShown = filteredMine.length + filteredShared.length;

  const sidebarClass = sidebarOpen
    ? styles.sidebar
    : `${styles.sidebar} ${styles.sidebarHidden}`;

  return (
    <aside className={sidebarClass}>
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${sidebarTab === 'threads' ? styles.activeTab : ''}`}
          onClick={() => onTabChange('threads')}
        >
          Threads
        </button>
        <button
          className={`${styles.tab} ${sidebarTab === 'knowledge' ? styles.activeTab : ''}`}
          onClick={() => onTabChange('knowledge')}
        >
          Knowledge
        </button>
      </div>

      {sidebarTab === 'threads' && (
        <>
          <div className={styles.searchWrap}>
            <input
              className={styles.search}
              placeholder="Search threads..."
              value={search.query}
              onChange={(e) => search.setQuery(e.target.value)}
            />
          </div>

          {search.active ? (
            <ThreadSearchResults
              results={search.results}
              totalMatches={search.totalMatches}
              loading={search.loading}
              error={search.error}
              query={trimmed}
              isAdmin={isAdmin}
              activeThreadId={activeThreadId}
              onSelect={onSelectThread}
            />
          ) : (
            <div className={styles.threadList}>
              {filteredMine.length > 0 && (
                <div className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <span>My Threads</span>
                    <span className={styles.sectionCount}>{filteredMine.length}</span>
                  </div>
                  {filteredMine.map((thread) => (
                    <JobThreadItem
                      key={thread.id}
                      thread={thread}
                      active={thread.id === activeThreadId}
                      onClick={() => onSelectThread(thread.id)}
                      onDelete={onDeleteThread}
                      onShare={onShareThread}
                      onRename={onRenameThread}
                    />
                  ))}
                </div>
              )}

              {filteredShared.length > 0 && (
                <div className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <span>Shared with me</span>
                    <span className={styles.sectionCount}>{filteredShared.length}</span>
                  </div>
                  {filteredShared.map((thread) => (
                    <JobThreadItem
                      key={thread.id}
                      thread={thread}
                      active={thread.id === activeThreadId}
                      onClick={() => onSelectThread(thread.id)}
                    />
                  ))}
                </div>
              )}

              {totalShown === 0 && (
                <div className={styles.empty}>
                  {trimmed ? 'No threads match.' : 'No threads yet — start a new chat.'}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {sidebarTab === 'knowledge' && (
        <KnowledgePanel
          entries={knowledgeEntries}
          searchQuery={knowledgeSearch}
          onSearch={(query) => {
            setKnowledgeSearch(query);
            onSearchKnowledge(query);
          }}
        />
      )}
    </aside>
  );
}
